import { isNil } from "lodash";
import { decodeStringLiteral, STRING_LITERAL_SOURCE, toStringLiteral } from "./string-literal";

/**
 * Rule/task properties whose value is a list of process roles: those of every task, the recipients of an e-mail send
 * task and the roles a role setup task has assigned. A rule can set any of them (`ownerRoles` is export-only).
 */
export const ROLE_VALUED_PROPERTIES: ReadonlySet<string> = new Set([
  "targetRoles",
  "responsibleRoles",
  "visibleForRoles",
  "recipientRoles",
  "setupRoles",
]);

const STRING_LITERAL = new RegExp(STRING_LITERAL_SOURCE, "g");

/** Matches an expression that is nothing but a literal array of strings, e.g. `['Arbeitgeber', "Kandidat"]`. */
const STRING_ARRAY_LITERAL = new RegExp(
  String.raw`^\[\s*(?:(?:${STRING_LITERAL_SOURCE})\s*,\s*)*(?:(?:${STRING_LITERAL_SOURCE})\s*)?\]$`
);

/**
 * Matches the second argument of `func.getOrganizationId(<process>, '<role>')` when it is a
 * string literal. Non-literal arguments (variables, concatenations) are skipped — they cannot
 * be checked statically and must not produce false rejections.
 */
const ROLE_LITERAL_IN_EXPRESSION = new RegExp(
  String.raw`(getOrganizationId\s*\([^,()]*,\s*)(${STRING_LITERAL_SOURCE})(\s*\))`,
  "g"
);

/**
 * Returns the role strings of an expression that is a plain string-array literal, or `null` when
 * the expression computes its value dynamically — those cannot be checked statically and must not
 * be rejected.
 */
export function extractRoleArrayLiteral(expression: string | undefined): string[] | null {
  const trimmed = expression?.trim();
  if (!trimmed || !STRING_ARRAY_LITERAL.test(trimmed)) return null;
  return Array.from(trimmed.matchAll(STRING_LITERAL), ([literal]) => decodeStringLiteral(literal));
}

/** Extracts the statically known role literals an expression passes to `getOrganizationId`. */
export function extractRoleLiterals(expression: string): string[] {
  return Array.from(expression.matchAll(ROLE_LITERAL_IN_EXPRESSION), ([, , literal]) => decodeStringLiteral(literal));
}

/** Replaces the role literals an expression passes to `getOrganizationId` (see {@link extractRoleLiterals}). */
export function replaceRoleLiterals(expression: string, newRoleKeys: ReadonlyMap<string, string>): string {
  // A callback, not a replacement string: role keys may contain `$`, which String.replace would interpret
  return expression.replace(
    ROLE_LITERAL_IN_EXPRESSION,
    (call: string, opening: string, literal: string, closing: string) => {
      const newRole = newRoleKeys.get(decodeStringLiteral(literal));
      return isNil(newRole) ? call : `${opening}${toStringLiteral(newRole, literal[0])}${closing}`;
    }
  );
}

/** Replaces the roles of a plain string-array literal (see {@link extractRoleArrayLiteral}); other expressions stay. */
export function replaceRoleArrayLiteral(expression: string, newRoleKeys: ReadonlyMap<string, string>): string {
  if (isNil(extractRoleArrayLiteral(expression))) {
    return expression;
  }

  return expression.replace(STRING_LITERAL, (literal: string) => {
    const newRole = newRoleKeys.get(decodeStringLiteral(literal));
    return isNil(newRole) ? literal : toStringLiteral(newRole, literal[0]);
  });
}
